import * as Comlink from 'comlink';
import type { Analyzer } from './analyzer';
import MagicString from './magic';
import { ModuleGraph } from './module-graph';
import { isScript } from './resolver';
import { transformClosingBrackets } from './transforms/closing-brackets';
import { transformExports } from './transforms/exports';
import { transformExpoFrameworkReady } from './transforms/framework-ready';
import { transformLucide, clearIcons } from './transforms/lucide';
import { transformTSWrap } from './transforms/ts-wrap';
import { transformUnescapedQuotes } from './transforms/unescaped-quotes';
import { analysisWatcher } from './watcher';
import type { BoltAction } from '~/lib/runtime/actions';
import { trackDurationOrTimeout } from '~/lib/static-analysis/tracking';
import { workbenchStore } from '~/lib/stores/workbench';
import { logger } from '~/utils/logger';
import { watchAll } from '~/utils/nanostore';

export function initCodeAnalysis() {
  let isStreamingOrFixing = false;

  // wait for streaming and fixing to end
  watchAll(workbenchStore.isStreaming, workbenchStore.isFixing).subscribe(([isStreaming, isFixing]) => {
    if (isStreaming || isFixing) {
      isStreamingOrFixing = true;
    } else if (isStreamingOrFixing) {
      isStreamingOrFixing = false;

      workbenchStore.isAnalyzing.set(true);

      analyze()
        .catch(console.error)
        .finally(() => {
          workbenchStore.isAnalyzing.set(false);
        });
    }
  });
}

const worker = new Worker(new URL('./analyzer.ts', import.meta.url), {
  type: 'module',
});

const analyzer = Comlink.wrap<Analyzer>(worker);

const graph = new ModuleGraph(analyzer);

analysisWatcher.addListener((changes) => {
  return graph.handleChanges(changes);
});

async function analyze() {
  const sendAnalyzeDurationEvent = trackDurationOrTimeout();

  const actions = await waitAndGetLastActions();

  // make sure the watcher has seen all file writes
  await analysisWatcher.wait();

  // now files and installs are complete, we can wait for the graph
  await graph.wait();

  const fileActions = actions.filter((action) => action.type === 'file');

  const files = fileActions.map((action) => [action.actionDetails.get(), action.content.get()]);

  if (import.meta.env.DEV) {
    logger.time('analysis');
  }

  await Promise.all(
    files.map(async ([path, content]) => {
      if (path && isScript(path) && content) {
        const code = new MagicString(content);
        const moduleDeclarations = graph.getModuleDeclarations(path);

        await transformUnescapedQuotes(code, analyzer);
        await transformExpoFrameworkReady(code, moduleDeclarations, path);
        await transformLucide(code, moduleDeclarations);
        await transformTSWrap(code, path);
        await transformExports(code, path, graph, analyzer);
        await transformClosingBrackets(code, path);

        if (code.hasChanges) {
          await workbenchStore.writeFile(path, {
            type: 'file',
            content: code.toString(),
          });

          workbenchStore.reloadPreview();
        }
      }
    }),
  );

  if (import.meta.env.DEV) {
    logger.timeEnd('analysis');
  }

  clearIcons();

  sendAnalyzeDurationEvent();
}

async function waitAndGetLastActions() {
  // a Set is ordered by insertion order, so this should be correct
  const lastArtifact = workbenchStore.artifactList.at(-1);

  if (!lastArtifact) {
    return [];
  }

  const getActions = () => {
    const actions = [...(lastArtifact.runner.actions.values() ?? [])];

    // wait for "install" and "file" actions
    return actions.filter((action) => ['install', 'file'].includes(action.type));
  };

  // wait for artifact to finish
  return new Promise<BoltAction[]>((resolve) => {
    // check if they've already finished
    const actions = getActions();

    const complete = actions.every((action) => {
      const status = action.status.get();

      return !(status === 'running' || status === 'pending');
    });

    if (complete) {
      resolve(actions);
      return;
    }

    const dispose = lastArtifact.runner.stateUpdates.subscribe(() => {
      const actions = getActions();

      for (const action of actions) {
        const status = action.status.get();

        if (status === 'running' || status === 'pending') {
          return;
        }
      }

      dispose();
      resolve(actions);
    });
  });
}
